import React from 'react';
import { message } from 'antd';
import { User } from '../../../data';
import { can } from '../../../core/access';
import { t } from '../../../company/labels';
import { isUserTypeEnabled } from '../../../company/session';

// All assignable roles of the signed-in company's backend. Trade Marketing users may only
// create Promotor / Ubicación.
const ALL_ROLE_OPTIONS = [
    { value: 'gerente', label: 'Gerente' },
    { value: 'asesor', label: 'Asesor' },
    { value: 'promotor', label: t('promotor') },
    { value: 'auditor', label: 'Auditor' },
    { value: 'analista', label: `Supervisor ${t('promotor')}` },
    { value: 'location', label: 'Ubicación' },
    { value: 'admin', label: 'Admin' },
    { value: 'TradeMarketing', label: 'Trade Marketing' },
    { value: 'auditAnalyst', label: 'Análisis Auditor' },
    { value: 'client', label: 'Cliente' },
    { value: 'route', label: 'Ruta' }
].filter((option) => isUserTypeEnabled(option.value));
const TRADE_MARKETING_ROLE_OPTIONS = ALL_ROLE_OPTIONS.filter((o) => ['promotor', 'location'].includes(o.value));

// Route-store option label: full store name plus RIF, so a piso can be found by either.
export const storeLabel = (store) => {
    if (!store) return '';
    const rif = store.rif ? ` · RIF ${store.rif}` : '';
    return `${store.name || store._id}${rif}`;
};
// Match a route-store option on name, RIF or client name (case-insensitive substring).
export const storeMatches = (input, store) => {
    const q = String(input || '').trim().toLowerCase();
    if (!q) return true;
    return [store?.name, store?.rif, store?.clientName].some((v) => String(v || '').toLowerCase().includes(q));
};

/** Form state, route-store loading and submit validation for the create / edit user drawer. */
export function useUserDrawer({ isOpen, setIsOpen, selectedUser, setSelectedUser, createOrUpdateUser, fetchStoresByClient, fetchStoreById }) {
    const [name, setName] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [channel, setChannel] = React.useState('');
    const [userType, setUserType] = React.useState('');
    const [rif, setRif] = React.useState('');
    const [active, setActive] = React.useState(true);
    const [loading, setLoading] = React.useState(false);
    const [clientId, setClientId] = React.useState(null);
    const [routeStores, setRouteStores] = React.useState([]);
    const [availableStores, setAvailableStores] = React.useState([]);
    const [storesLoading, setStoresLoading] = React.useState(false);
    // Stores already on the route that are NOT in `availableStores` (inactive, or belonging to
    // another client than the one currently selected), resolved individually so the route list
    // always shows the real store name instead of its id.
    const [resolvedStores, setResolvedStores] = React.useState({});

    const isEdit = !!selectedUser;
    const roleOptions = can(User.getInstance()?.userType, 'users.manage-all-roles') ? ALL_ROLE_OPTIONS : TRADE_MARKETING_ROLE_OPTIONS;

    // Load stores when clientId changes for route users
    React.useEffect(() => {
        if (userType !== 'route' || !clientId || !fetchStoresByClient) {
            setAvailableStores([]);
            return;
        }
        let cancelled = false;
        setStoresLoading(true);
        fetchStoresByClient(clientId)
            .then((stores) => {
                if (!cancelled) setAvailableStores(Array.isArray(stores) ? stores : []);
            })
            .catch(() => {
                if (!cancelled) setAvailableStores([]);
            }) // 404 = client has no active stores
            .finally(() => {
                if (!cancelled) setStoresLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [clientId, userType, fetchStoresByClient]);

    // Resolve names for route stores the client list doesn't cover.
    React.useEffect(() => {
        if (userType !== 'route' || storesLoading || !fetchStoreById) return;
        const known = new Set(availableStores.map((s) => String(s._id)));
        const missing = routeStores.filter((id) => !known.has(String(id)) && !resolvedStores[id]);
        if (!missing.length) return;
        let cancelled = false;
        Promise.all(
            missing.map((id) =>
                Promise.resolve(fetchStoreById(id))
                    .then((store) => [id, store && store._id ? store : null])
                    .catch(() => [id, null])
            )
        ).then((entries) => {
            if (cancelled) return;
            setResolvedStores((prev) => {
                const next = { ...prev };
                entries.forEach(([id, store]) => {
                    if (store) next[id] = store;
                });
                return next;
            });
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeStores, availableStores, storesLoading, userType, fetchStoreById]);

    const findRouteStore = (storeId) => availableStores.find((s) => String(s._id) === String(storeId)) || resolvedStores[storeId] || null;

    // Load user data when editing
    React.useEffect(() => {
        if (isEdit && selectedUser) {
            setName(selectedUser.name || '');
            setEmail(selectedUser.email || '');
            setChannel(selectedUser.channel || '');
            setUserType(selectedUser.userType || '');
            setRif(selectedUser.rif || '');
            setActive(selectedUser.active !== undefined ? selectedUser.active : true);
            setClientId(selectedUser.client || null);
            setRouteStores(Array.isArray(selectedUser.route) ? selectedUser.route.map((id) => id?.toString?.() ?? id) : []);
            setPassword('');
        } else {
            setName('');
            setEmail('');
            setPassword('');
            setChannel('');
            setUserType('');
            setRif('');
            setActive(true);
            setClientId(null);
            setRouteStores([]);
        }
    }, [isEdit, selectedUser, isOpen]);

    const onCloseDrawer = () => {
        setName('');
        setEmail('');
        setPassword('');
        setChannel('');
        setUserType('');
        setRif('');
        setActive(true);
        setClientId(null);
        setRouteStores([]);
        setAvailableStores([]);
        setResolvedStores({});
        setSelectedUser(null);
        setIsOpen(false);
        setLoading(false);
    };

    const onSubmit = async () => {
        setLoading(true);

        // For editing, password is optional (only update if provided)
        const requiredFields = isEdit ? [name, email, channel, userType] : [name, email, password, channel, userType];

        if (requiredFields.some((field) => !field)) {
            message.error(isEdit ? 'Nombre, correo, canal y tipo de usuario son requeridos' : 'Todos los campos son requeridos');
            setLoading(false);
            return;
        }

        // Validate email format and no spaces
        if (email.includes(' ')) {
            message.error('El correo electrónico no puede contener espacios');
            setLoading(false);
            return;
        }

        // Basic email format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            message.error('Por favor ingresa un correo electrónico válido');
            setLoading(false);
            return;
        }

        const payload = {
            name,
            email,
            channel,
            userType,
            rif,
            active,
            ...(userType === 'client' && clientId ? { client: clientId } : {}),
            ...(userType === 'route' ? { client: clientId || null, route: routeStores } : {})
        };

        // Only include password if it's provided (for updates) or if it's a new user
        if (!isEdit || password) {
            payload.password = password;
        }

        // On failure (e.g. 409 'Este correo no está disponible.') the message is already shown;
        // keep the drawer open so the email can be corrected.
        const saved = await createOrUpdateUser(payload);
        if (saved === false) {
            setLoading(false);
            return;
        }
        onCloseDrawer();
    };

    // Field handlers that carry side effects.
    const onEmailChange = (value) => setEmail(value.replace(/\s/g, ''));
    const onUserTypeChange = (e) => {
        setUserType(e);
        if (e !== 'client' && e !== 'route') {
            setClientId(null);
        }
        setRouteStores([]);
        setAvailableStores([]);
    };
    const onRouteClientChange = (val) => {
        setClientId(val);
        setRouteStores([]);
    };
    const addRouteStore = (storeId) => {
        if (!routeStores.includes(storeId)) {
            setRouteStores((prev) => [...prev, storeId]);
        }
    };
    const removeRouteStore = (storeId) => setRouteStores((prev) => prev.filter((id) => id !== storeId));

    const storeOptions = availableStores.filter((s) => !routeStores.includes(s._id)).map((s) => ({ value: s._id, label: storeLabel(s), store: s }));

    return {
        isEdit,
        roleOptions,
        name,
        setName,
        email,
        onEmailChange,
        password,
        setPassword,
        channel,
        setChannel,
        userType,
        onUserTypeChange,
        rif,
        setRif,
        active,
        setActive,
        loading,
        clientId,
        setClientId,
        onRouteClientChange,
        routeStores,
        addRouteStore,
        removeRouteStore,
        storeOptions,
        storesLoading,
        findRouteStore,
        onCloseDrawer,
        onSubmit
    };
}
